import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowUpRight, X } from "lucide-react";
import { availabilityResources, availabilityMonth, extractApiError } from "@/lib/api";
import { toast } from "sonner";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const AVAILABILITY_DISCLAIMER = "Availability is subject to change until your retainer is paid and your service agreement is signed. Submitting an inquiry does not reserve your date.";

export default function CheckAvailability() {
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [resource, setResource] = useState(null);
  const [cal, setCal] = useState(null);
  const [loading, setLoading] = useState(false);
  const now = new Date();
  const [month, setMonth] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await availabilityResources();
        setResources(r.resources);
      } catch (e) { toast.error(extractApiError(e, "Could not load services")); }
    })();
  }, []);

  useEffect(() => {
    if (!resource) return;
    (async () => {
      setLoading(true);
      setSelectedDate(null);
      try {
        setCal(await availabilityMonth(resource.key, month.y, month.m));
      } catch (e) { toast.error(extractApiError(e, "Could not load availability")); }
      finally { setLoading(false); }
    })();
  }, [resource, month.y, month.m]);

  const prev = () => setMonth((m) => (m.m === 1 ? { y: m.y - 1, m: 12 } : { ...m, m: m.m - 1 }));
  const next = () => setMonth((m) => (m.m === 12 ? { y: m.y + 1, m: 1 } : { ...m, m: m.m + 1 }));
  const jumpToday = () => setMonth({ y: new Date().getFullYear(), m: new Date().getMonth() + 1 });

  const todayIso = new Date().toISOString().slice(0, 10);
  const unavailableSet = new Set((cal && cal.unavailable_dates) || []);

  const goToInquiry = () => {
    if (!selectedDate || !resource) return;
    navigate(`/book?service=${encodeURIComponent(resource.key)}&date=${encodeURIComponent(selectedDate)}`);
  };

  return (
    <div data-testid="availability-page" className="pt-32 md:pt-44 pb-24 mx-auto max-w-[1200px] px-5 md:px-10">
      <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Check Availability</div>
      <h1 className="mt-4 font-display font-light tracking-tighter text-5xl md:text-7xl leading-none">Pick a service.</h1>
      <p className="mt-6 font-body text-base md:text-xl max-w-2xl" style={{ color: "var(--ss-cream-dim)" }}>
        See which Simply Social dates are open for the service you're after, then send us a quick inquiry to lock it in.
      </p>

      <div className="mt-10 grid sm:grid-cols-2 gap-3" data-testid="availability-resources">
        {resources.map((r) => {
          const selected = resource && resource.key === r.key;
          return (
            <button
              key={r.key}
              data-testid={`availability-resource-${r.key}`}
              onClick={() => { setResource(r); setMonth({ y: new Date().getFullYear(), m: new Date().getMonth() + 1 }); }}
              className="text-left p-5 border transition-colors"
              style={{
                borderColor: selected ? r.color : "var(--ss-border)",
                background: selected ? `${r.color}11` : "transparent",
              }}>
              <div className="flex items-center gap-3">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: r.color }} />
                <div>
                  <div className="font-display text-xl md:text-2xl" style={{ color: selected ? r.color : "var(--ss-cream)" }}>{r.label}</div>
                  <div className="font-label text-[0.65rem] mt-1" style={{ color: "var(--ss-cream-dim)" }}>{r.subtitle}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {resource && (
        <div className="mt-10" data-testid="availability-calendar">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
            <div className="flex items-center gap-2">
              <button data-testid="avail-prev" onClick={prev} className="btn-outline px-3 py-2"><ChevronLeft size={16} /></button>
              <div className="font-display text-2xl md:text-3xl px-3">{MONTHS[month.m - 1]} {month.y}</div>
              <button data-testid="avail-next" onClick={next} className="btn-outline px-3 py-2"><ChevronRight size={16} /></button>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="avail-today" onClick={jumpToday} className="btn-outline">Today</button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 mb-4 text-xs" style={{ color: "var(--ss-cream-dim)" }}>
            <span className="inline-flex items-center gap-2"><span className="inline-block w-2.5 h-2.5" style={{ background: resource.color }} /> Available</span>
            <span className="inline-flex items-center gap-2"><span className="inline-block w-2.5 h-2.5 border" style={{ borderColor: "var(--ss-cream-dim)" }} /> Unavailable</span>
            <span className="inline-flex items-center gap-2"><span className="inline-block w-2.5 h-2.5" style={{ background: "var(--ss-cream-dim)", opacity: 0.3 }} /> Past</span>
          </div>

          {loading && <p style={{ color: "var(--ss-cream-dim)" }}>Loading…</p>}

          {cal && (
            <div className="grid grid-cols-7 gap-px p-px border" style={{ borderColor: "var(--ss-border)", background: "var(--ss-border)" }}>
              {WEEKDAYS.map((w) => (
                <div key={w} className="font-label text-[0.6rem] tracking-widest px-1 py-2 text-center" style={{ background: "var(--ss-bg)", color: "var(--ss-cream-dim)" }}>{w}</div>
              ))}
              {Array.from({ length: cal.first_day_weekday }).map((_, i) => (
                <div key={`blank-${i}`} style={{ background: "var(--ss-bg)", minHeight: 56 }} />
              ))}
              {Array.from({ length: cal.days_in_month }).map((_, i) => {
                const dayNum = i + 1;
                const dateStr = `${cal.year}-${String(cal.month).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
                const isPast = dateStr < (cal.today || todayIso);
                const isUnavail = unavailableSet.has(dateStr);
                const isSelected = selectedDate === dateStr;
                const clickable = !isPast && !isUnavail;
                let bg = "var(--ss-bg)";
                let fg = "var(--ss-cream)";
                let border = "none";
                let opacity = 1;
                if (isPast) { opacity = 0.35; }
                else if (isUnavail) { fg = "var(--ss-cream-dim)"; }
                else if (isSelected) { bg = resource.color; fg = "#F5F1E8"; }
                else { border = `1px solid ${resource.color}`; }
                return (
                  <button
                    key={dateStr}
                    data-testid={`avail-day-${dateStr}`}
                    disabled={!clickable}
                    aria-label={`${dateStr} ${isUnavail ? "unavailable" : clickable ? "available" : "past"}`}
                    onClick={() => clickable && setSelectedDate(dateStr)}
                    className="relative text-left px-2 py-3 md:py-4"
                    style={{ background: bg, color: fg, opacity, border, cursor: clickable ? "pointer" : "not-allowed", minHeight: 56 }}>
                    <div className="font-display text-base md:text-lg">{dayNum}</div>
                    <div className="font-label text-[0.55rem] tracking-widest mt-1" style={{ color: isSelected ? "#F5F1E8" : (isUnavail ? "var(--ss-cream-dim)" : (clickable ? resource.color : "var(--ss-muted)")) }}>
                      {isPast ? "" : isUnavail ? "UNAVAIL." : "OPEN"}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Request-this-date CTA */}
          <div className="mt-6 p-5 border" style={{ borderColor: "var(--ss-rust)", background: "rgba(160,30,42,0.05)" }}>
            {selectedDate ? (
              <div className="flex flex-wrap items-center gap-4 justify-between">
                <div>
                  <div className="font-label text-[0.65rem]" style={{ color: "var(--ss-rust)" }}>Selected</div>
                  <div className="font-display text-xl md:text-2xl mt-1">{resource.label} · {selectedDate}</div>
                </div>
                <button
                  data-testid="avail-request-date-btn"
                  onClick={goToInquiry}
                  className="btn-primary">
                  Request This Date <ArrowUpRight className="arrow" size={14} />
                </button>
              </div>
            ) : (
              <div style={{ color: "var(--ss-cream-dim)" }}>Tap an open date to send an inquiry for it.</div>
            )}
          </div>

          <p className="mt-4 text-xs md:text-sm italic" style={{ color: "var(--ss-cream-dim)" }} data-testid="avail-disclaimer">
            {AVAILABILITY_DISCLAIMER}
          </p>

          <div className="mt-10">
            <Link to="/book" data-testid="avail-general-inquiry-link" className="btn-outline">
              Prefer to just inquire? Open the full form <ArrowUpRight size={14} className="arrow" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
